/**
 * Adjuntos cifrados de un secreto (patrón de media E2E, estilo Susurro).
 *
 * Cada adjunto usa una **clave de contenido por-adjunto** (AES-256-GCM, 32 B
 * aleatorios) que cifra los BYTES del fichero. Esa clave de contenido se
 * ENVUELVE con la clave del vault (nunca viaja en claro). Los metadatos
 * sensibles (nombre de fichero, tipo MIME) también se cifran a un sobre con la
 * clave del vault. Lo que el servidor custodia es 100% opaco:
 *
 *   fileBytes ─AES-GCM(contentKey, AAD=attachment)──▶ blob            (→ server, opaco)
 *   contentKey ─AES-GCM(vaultKey,  AAD=attachment-key)▶ wrappedKey    (→ server, opaco)
 *   {name,mime} ─AES-GCM(vaultKey, AAD=attachment-meta)▶ metaEnvelope (→ server, opaco)
 *
 * El AAD ata cada criptograma a `vaultId:attachmentId`: el servidor no puede
 * mover un blob/clave/metadato a otro adjunto o vault sin que el open falle.
 * Descarga+descifrado ocurren SOLO en el cliente (el server nunca ve el plano).
 */

import { base64ToBytes, bytesToBase64, utf8Decode, utf8Encode } from "./encoding";
import { openBase64, randomSymmetricKey, sealBase64 } from "./primitives";
import type { AttachmentMeta, ProtectedAttachment } from "./types";

function contentAad(vaultId: string, attachmentId: string): string {
  return `boveda:attachment:v1:${vaultId}:${attachmentId}`;
}
function keyAad(vaultId: string, attachmentId: string): string {
  return `boveda:attachment-key:v1:${vaultId}:${attachmentId}`;
}
function metaAad(vaultId: string, attachmentId: string): string {
  return `boveda:attachment-meta:v1:${vaultId}:${attachmentId}`;
}

export interface EncryptAttachmentInput {
  readonly vaultKey: Uint8Array;
  readonly vaultId: string;
  readonly itemId: string;
  readonly attachmentId: string;
  readonly bytes: Uint8Array;
  readonly meta: AttachmentMeta;
}

/**
 * Cifra un fichero a un {@link ProtectedAttachment} (todo opaco para el server).
 * `size` es el tamaño del PLANO en bytes: metadato mínimo no sensible (igual
 * que el `vault_id` de asociación), útil para la UI sin descifrar.
 */
export async function encryptAttachment(
  input: EncryptAttachmentInput,
): Promise<ProtectedAttachment> {
  const { vaultKey, vaultId, itemId, attachmentId, bytes, meta } = input;
  const contentKey = randomSymmetricKey();
  try {
    const blob = await sealBase64(contentKey, bytes, contentAad(vaultId, attachmentId));
    const wrappedKey = await sealBase64(vaultKey, contentKey, keyAad(vaultId, attachmentId));
    const metaBytes = utf8Encode(JSON.stringify(meta));
    const metaEnvelope = await sealBase64(vaultKey, metaBytes, metaAad(vaultId, attachmentId));
    return {
      id: attachmentId,
      vaultId,
      itemId,
      wrappedKey,
      metaEnvelope,
      blob,
      size: bytes.length,
    };
  } finally {
    // Cero la clave de contenido tras usarla (no debe quedar en memoria).
    contentKey.fill(0);
  }
}

export interface DecryptedAttachment {
  readonly meta: AttachmentMeta;
  readonly bytes: Uint8Array;
}

/** Descifra un adjunto: desenvuelve la clave de contenido y abre blob + metadatos. */
export async function decryptAttachment(
  vaultKey: Uint8Array,
  attachment: ProtectedAttachment,
): Promise<DecryptedAttachment> {
  const { vaultId, id } = attachment;
  const contentKey = await openBase64(vaultKey, attachment.wrappedKey, keyAad(vaultId, id));
  try {
    const bytes = await openBase64(contentKey, attachment.blob, contentAad(vaultId, id));
    const metaBytes = await openBase64(vaultKey, attachment.metaEnvelope, metaAad(vaultId, id));
    // Trust boundary: el sobre lo selló ESTE cliente (AEAD ya autenticó el JSON).
    // oxlint-disable-next-line typescript-eslint/no-unsafe-type-assertion
    const meta = JSON.parse(utf8Decode(metaBytes)) as AttachmentMeta;
    return { meta, bytes };
  } finally {
    contentKey.fill(0);
  }
}

/** Solo los metadatos (nombre/MIME) sin traer/descifrar el blob completo. */
export async function decryptAttachmentMeta(
  vaultKey: Uint8Array,
  attachment: Pick<ProtectedAttachment, "id" | "vaultId" | "metaEnvelope">,
): Promise<AttachmentMeta> {
  const metaBytes = await openBase64(
    vaultKey,
    attachment.metaEnvelope,
    metaAad(attachment.vaultId, attachment.id),
  );
  // oxlint-disable-next-line typescript-eslint/no-unsafe-type-assertion
  return JSON.parse(utf8Decode(metaBytes)) as AttachmentMeta;
}

export { base64ToBytes, bytesToBase64 };
